/**
 * Export time: choose a format and period, check the Billing IDs the file
 * needs (fixing them in place or leaving entries out), preview the first
 * lines, then create a batch that locks the entries and downloads the file.
 */
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CircleCheck,
  Download,
  Loader2,
  Lock
} from "lucide-react";
import { useState, type FC } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { exportActions, reviewActions, timeActions } from "../actions";
import { useExportPreview, useProviders, useTimeEntry } from "../queries";
import type {
  ExportCompanyTerms,
  ExportFormat,
  ExportIssue,
  ExportPreview,
  ExportRequest,
  TimekeeperClass
} from "../types";
import { formatAmount, formatHours, formatMoney, todayIso } from "../utils";
import { CellInput } from "./BillingIdsTab";
import {
  EXPORT_FORMATS,
  fixFor,
  formatLabel,
  isLedes,
  issueHint,
  issueTitle,
  periodLabel,
  plural,
  type Provider
} from "./exportModel";
import { CLASSIFICATIONS, RANGES, rangeFor, type RangeId } from "./reviewModel";

const STEPS = ["Format", "Check", "Preview"] as const;
type Step = 1 | 2 | 3;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialRange: RangeId;
  requireApproval: boolean;
  onOpenSettings: () => void;
};

const Stepper: FC<{ step: Step }> = ({ step }) => (
  <span className="inline-flex items-center gap-1.5">
    {STEPS.map((s, i) => (
      <span key={s} className="inline-flex items-center gap-1.5">
        {i ? <span className="h-px w-4 bg-border" /> : null}
        <span
          className={cn(
            "inline-flex size-4.5 items-center justify-center rounded-full text-sm font-medium",
            step > i + 1
              ? "bg-primary text-primary-foreground"
              : step === i + 1
              ? "bg-foreground text-background"
              : "bg-fill-2 text-fg-3"
          )}
        >
          {step > i + 1 ? (
            <Check className="size-2.5" strokeWidth={3} />
          ) : (
            i + 1
          )}
        </span>
        <span
          className={cn(
            "text-sm font-medium",
            step === i + 1 ? "text-fg" : "text-fg-3"
          )}
        >
          {s}
        </span>
      </span>
    ))}
  </span>
);

const CompanyTerms: FC<{ companies: ExportCompanyTerms[] }> = ({
  companies
}) => (
  <div
    className="flex flex-col gap-1.5 rounded-xl border border-line-2 px-3 py-2.5"
    data-testid="export-company-terms"
  >
    <span className="text-sm font-medium text-fg-3">
      Company terms in this export
    </span>
    {companies.map((c) => (
      <span
        key={c.id}
        className="flex flex-wrap items-center gap-1.5 text-base"
      >
        <span className="font-medium">{c.name}</span>
        <span className="text-sm text-fg-3">
          {plural(c.cases, "case", "cases")}
        </span>
        <Badge variant="secondary" className="font-normal">
          {c.format_label}
        </Badge>
        <Badge variant="secondary" className="font-normal">
          {c.grouping === "entity"
            ? "One invoice per entity"
            : "One invoice per matter"}
        </Badge>
        {c.require_client_matter_id ? (
          <Badge variant="secondary" className="font-normal">
            Client Matter ID Required
          </Badge>
        ) : null}
        {c.require_po ? (
          <Badge variant="secondary" className="font-normal">
            PO Required
          </Badge>
        ) : null}
        {c.ebilling_system ? (
          <span className="text-sm text-fg-3">
            Uploads to {c.ebilling_system}
          </span>
        ) : null}
      </span>
    ))}
  </div>
);

const FormatStep: FC<{
  format: ExportFormat;
  setFormat: (f: ExportFormat) => void;
  range: RangeId;
  setRange: (r: RangeId) => void;
  period: string;
  requireApproval: boolean;
  includeReleased: boolean;
  setIncludeReleased: (v: boolean) => void;
  providers: Provider[];
  companies: ExportCompanyTerms[];
}> = ({
  format,
  setFormat,
  range,
  setRange,
  period,
  requireApproval,
  includeReleased,
  setIncludeReleased,
  providers,
  companies
}) => (
  <>
    <div
      role="radiogroup"
      aria-label="Format"
      className="grid grid-cols-3 gap-2.5"
    >
      {EXPORT_FORMATS.map((f) => {
        const on = f.id === format;
        return (
          <button
            key={f.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => setFormat(f.id)}
            className={cn(
              "flex min-h-24 flex-col gap-1.5 rounded-xl border p-3 text-left transition-colors",
              on
                ? "border-primary bg-primary/5"
                : "border-line-2 hover:bg-fill-1"
            )}
          >
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex size-3.5 items-center justify-center rounded-full border",
                  on ? "border-primary" : "border-line-3"
                )}
              >
                {on ? (
                  <span className="size-2 rounded-full bg-primary" />
                ) : null}
              </span>
              <span className="text-base font-medium">{f.title}</span>
            </span>
            <span className="text-sm leading-4.25 text-fg-3">{f.desc}</span>
          </button>
        );
      })}
      {providers.map((p) => (
        <div
          key={p.id}
          aria-disabled
          className="flex min-h-24 flex-col gap-1.5 rounded-xl border border-line-2 p-3 opacity-55"
        >
          <span className="flex items-center gap-2">
            <span className="text-base font-medium">{p.name}</span>
            <span className="flex-1" />
            <Badge variant="secondary">Coming Soon</Badge>
          </span>
          <span className="text-sm leading-4.25 text-fg-3">{p.what}</span>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-[140px_minmax(0,1fr)] items-center gap-y-3 pt-1 text-base">
      <span className="text-fg-3">Period</span>
      <span className="flex items-center gap-2">
        <Select value={range} onValueChange={(v) => setRange(v as RangeId)}>
          <SelectTrigger
            size="sm"
            aria-label="Period"
            className="h-7 w-40 text-sm"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {range === "all" ? null : (
          <span className="text-sm text-fg-3">{period}</span>
        )}
      </span>
      <span className="text-fg-3">Include</span>
      <span className="flex items-center gap-3.5">
        <span className="inline-flex items-center gap-1.5">
          <Checkbox checked disabled aria-label="Approved" /> Approved
        </span>
        {requireApproval ? (
          <span className="inline-flex items-center gap-1.5">
            <Checkbox
              id="export-include-released"
              checked={includeReleased}
              onCheckedChange={(v) => setIncludeReleased(v === true)}
            />
            <Label
              htmlFor="export-include-released"
              className="cursor-pointer font-normal"
            >
              Released, not yet approved
            </Label>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <Checkbox checked disabled aria-label="Released" /> Released (your
            firm doesn&apos;t require approval)
          </span>
        )}
      </span>
    </div>
    {companies.length ? <CompanyTerms companies={companies} /> : null}
  </>
);

const CASE_FIELD_LABEL = {
  matter_no: "Matter no.",
  client_no: "Client no.",
  client_matter_id: "Client matter ID",
  po: "PO",
  employee_client_no: "Employee's client no."
} as const;

const IssueFixControl: FC<{
  issue: ExportIssue;
  onLeaveOut: (ids: string[]) => void;
  onOpenSettings: () => void;
}> = ({ issue, onLeaveOut, onOpenSettings }) => {
  const ids = issue.entry_ids ?? [];
  if (issue.fix?.action === "price_at_approved_rate")
    return (
      <Button
        variant="secondary"
        size="xs"
        onClick={() => void exportActions.priceAtApprovedRate(ids)}
      >
        {issue.fix.label}
      </Button>
    );
  if (issue.fix?.action === "leave_out")
    return (
      <Button variant="ghost" size="xs" onClick={() => onLeaveOut(ids)}>
        {issue.fix.label}
      </Button>
    );
  const fix = fixFor(issue);
  const leaveOut =
    issue.severity === "error" && issue.entry_ids?.length ? (
      <Button
        variant="ghost"
        size="xs"
        onClick={() => onLeaveOut(issue.entry_ids ?? [])}
      >
        Leave out {issue.entry_ids.length}
      </Button>
    ) : null;
  if (!fix) return leaveOut;
  if (fix.field === "settings")
    return (
      <span className="flex w-45 items-center gap-1">
        <CellInput
          label="Firm LEDES ID"
          placeholder="47-1234567"
          mono
          onSave={(v) => void reviewActions.saveFirmLedesId(v)}
        />
        <Button variant="ghost" size="xs" onClick={onOpenSettings}>
          Settings
        </Button>
      </span>
    );
  if (fix.field === "classification")
    return (
      <span className="flex items-center gap-1">
        <Select
          onValueChange={(v) =>
            void reviewActions.saveTimekeeper(fix.userId, {
              classification: v as TimekeeperClass
            })
          }
        >
          <SelectTrigger
            size="sm"
            aria-label={`Classification for ${issue.label}`}
            className="h-7 w-36 text-sm"
          >
            <SelectValue placeholder="Classification" />
          </SelectTrigger>
          <SelectContent>
            {CLASSIFICATIONS.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {leaveOut}
      </span>
    );
  const input =
    fix.field === "flat_fee" ? (
      <CellInput
        label={`Flat fee for ${issue.label}`}
        placeholder="2,500"
        prefix="$"
        onSave={(v) => {
          const fee = Number(v.replace(/[^0-9.]/g, ""));
          if (fee > 0)
            void reviewActions.saveCaseBilling(fix.caseId, { flat_fee: fee });
        }}
      />
    ) : fix.field === "rate" ? (
      <CellInput
        label={`Rate for ${issue.label}`}
        placeholder="350"
        prefix="$"
        onSave={(v) => {
          const rate = Number(v.replace(/[^0-9.]/g, ""));
          if (rate > 0) void reviewActions.saveTimekeeper(fix.userId, { rate });
        }}
      />
    ) : fix.field === "timekeeper_id" ? (
      <CellInput
        label={`Timekeeper ID for ${issue.label}`}
        placeholder="Timekeeper ID"
        mono
        onSave={(v) =>
          void reviewActions.saveTimekeeper(fix.userId, { timekeeper_id: v })
        }
      />
    ) : (
      <CellInput
        label={`${CASE_FIELD_LABEL[fix.field]} for ${issue.label}`}
        placeholder={CASE_FIELD_LABEL[fix.field]}
        mono
        onSave={(v) =>
          void reviewActions.saveCaseBilling(fix.caseId, {
            [fix.field]: v || null
          })
        }
      />
    );
  return (
    <span className="flex items-center gap-1">
      <span className="w-32.5">{input}</span>
      {leaveOut}
    </span>
  );
};

const NARRATIVES_SHOWN = 6;

const NarrativeInput: FC<{ id: string }> = ({ id }) => {
  const { data: entry } = useTimeEntry(id);
  if (!entry) return <Skeleton className="h-7 w-full" />;
  const [, m, d] = entry.work_date.split("-").map(Number);
  const who = entry.user_name?.split(" ")[0] ?? "";
  return (
    <span className="flex items-center gap-2">
      <span className="w-30 shrink-0 truncate text-sm text-fg-3 tabular-nums">
        {[who, `${m}/${d}`, formatHours(entry.duration_hours)]
          .filter(Boolean)
          .join(" · ")}
      </span>
      <span className="min-w-0 flex-1">
        <CellInput
          label={`Narrative for ${entry.user_name ?? "the entry"} on ${
            entry.work_date
          }`}
          placeholder="Write the invoice description"
          value={entry.narrative}
          onSave={(v) => {
            if (v) void timeActions.update(id, { narrative: v });
          }}
        />
      </span>
    </span>
  );
};

const NarrativeInputs: FC<{ ids: string[] }> = ({ ids }) => (
  <span className="mt-1.5 flex flex-col gap-1.5" data-testid="narrative-fix">
    {ids.slice(0, NARRATIVES_SHOWN).map((id) => (
      <NarrativeInput key={id} id={id} />
    ))}
    {ids.length > NARRATIVES_SHOWN ? (
      <span className="text-sm text-fg-3">
        And {ids.length - NARRATIVES_SHOWN} more. Write the rest on All entries.
      </span>
    ) : null}
  </span>
);

const CheckStep: FC<{
  format: ExportFormat;
  preview?: ExportPreview;
  excluded: string[];
  setExcluded: (fn: (prev: string[]) => string[]) => void;
  onOpenSettings: () => void;
}> = ({ format, preview, excluded, setExcluded, onOpenSettings }) => {
  if (!preview) return <Skeleton className="h-48" />;
  const errors = preview.issues.filter((i) => i.severity === "error");
  const warnings = preview.issues.filter((i) => i.severity === "warn");
  const b = preview.breakdown;
  const passed = [
    {
      title: `${plural(
        b.hourly,
        "hourly entry",
        "hourly entries"
      )} priced at timekeeper rates`,
      sub: "Rates from Billing IDs. Case overrides applied."
    },
    ...(b.flat_fee
      ? [
          {
            title: `${plural(
              b.flat_fee,
              "flat-fee entry",
              "flat-fee entries"
            )} exported as non-billable`,
            sub: "Amount 0.00 with hours kept, plus one line for each case's flat fee the first time it's exported."
          }
        ]
      : []),
    b.missing_activity
      ? null
      : {
          title: "All entries have UTBMS activity codes",
          sub: "Codes come from each entry; edit them on All entries."
        },
    preview.expense_count
      ? {
          title: `${plural(preview.expense_count, "expense", "expenses")} as ${
            isLedes(format) ? "E lines" : "expense lines"
          } with UTBMS expense codes`,
          sub: "Confirmed and approved case costs. Suggestions stay out until someone confirms them on the case."
        }
      : null
  ].filter((r): r is { title: string; sub: string } => !!r);

  return (
    <>
      <div className="flex gap-6 pb-1">
        {[
          { label: "Entries", value: String(preview.count) },
          { label: "Hours", value: formatHours(preview.hours) },
          ...(preview.expense_count
            ? [
                {
                  label: "Expenses",
                  value: formatAmount(preview.expense_amount ?? 0)
                }
              ]
            : []),
          { label: "Billable amount", value: formatMoney(preview.amount) }
        ].map((s) => (
          <span key={s.label} className="flex flex-col">
            <span className="text-sm text-fg-3">{s.label}</span>
            <span className="text-xl font-medium tabular-nums">{s.value}</span>
          </span>
        ))}
      </div>
      <div
        className="shrink-0 overflow-hidden rounded-xl border border-line-2"
        data-testid="export-checks"
      >
        {passed.map((r) => (
          <div
            key={r.title}
            className="flex gap-2.5 border-b border-line-2 px-3 py-2.5 last:border-b-0"
          >
            <CircleCheck className="mt-px size-3.75 text-success" />
            <span className="flex flex-col gap-0.5">
              <span className="text-base font-medium">{r.title}</span>
              <span className="text-sm text-fg-3">{r.sub}</span>
            </span>
          </div>
        ))}
        {[...errors, ...warnings].map((issue) => (
          <div
            key={`${issue.kind}-${issue.id}`}
            className={cn(
              "flex items-center gap-2.5 border-b border-line-2 px-3 py-2.5 last:border-b-0",
              issue.severity === "error" && "bg-warn-soft",
              issue.kind === "narrative" && "items-start"
            )}
            data-testid={`issue-${issue.kind}`}
          >
            <AlertTriangle className="size-3.75 shrink-0 text-warn" />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-base font-medium">{issueTitle(issue)}</span>
              <span className="text-sm text-fg-3">
                {issueHint(issue, format)}
              </span>
              {issue.kind === "narrative" && issue.entry_ids?.length ? (
                <NarrativeInputs ids={issue.entry_ids} />
              ) : null}
            </span>
            <IssueFixControl
              issue={issue}
              onOpenSettings={onOpenSettings}
              onLeaveOut={(ids) =>
                setExcluded((prev) => Array.from(new Set([...prev, ...ids])))
              }
            />
          </div>
        ))}
      </div>
      {excluded.length ? (
        <span className="flex items-center gap-2 text-sm text-fg-3">
          {plural(excluded.length, "entry", "entries")} left out of this batch.
          They stay unexported for a later one.
          <Button
            variant="ghost"
            size="xs"
            onClick={() => setExcluded(() => [])}
          >
            Put back
          </Button>
        </span>
      ) : null}
      {errors.length ? (
        <span className="flex items-center gap-1.5 text-sm text-warn">
          <AlertTriangle className="size-3.5" /> Resolve{" "}
          {plural(errors.length, "issue", "issues")} to continue. IDs you add
          are saved to Billing IDs for next time.
        </span>
      ) : warnings.length ? (
        <span className="text-sm text-fg-3">
          You can continue. Missing IDs leave those columns empty.
        </span>
      ) : preview.count ? (
        <span className="flex items-center gap-1.5 text-sm text-success">
          <CircleCheck className="size-3.5" /> Ready to export
        </span>
      ) : (
        <span className="text-sm text-fg-3">
          Nothing to export for this period.
        </span>
      )}
    </>
  );
};

const PreviewStep: FC<{ preview: ExportPreview }> = ({ preview }) => {
  const head = isLedes(preview.format) ? 2 : 1;
  const shown = Math.max(0, preview.sample.length - head);
  return (
    <>
      <span className="flex items-center gap-2 text-base">
        <span className="font-medium">{formatLabel(preview.format)}</span>
        <span className="text-fg-3">
          · {plural(preview.breakdown.invoices, "invoice", "invoices")} ·{" "}
          {plural(preview.breakdown.lines, "line", "lines")}
        </span>
        <span className="flex-1" />
        <span className="text-sm text-fg-3">
          First {shown} of {preview.breakdown.lines} lines
          {preview.format === "xlsx" ? " (as CSV columns)" : ""}
        </span>
      </span>
      <div
        className="overflow-x-auto rounded-xl border border-line-2 bg-fill-1 px-3 py-2.5"
        data-testid="export-sample"
      >
        {preview.sample.map((line, i) => (
          <div
            key={i}
            className={cn(
              "font-mono text-sm leading-4.5 whitespace-pre",
              i < head && "text-fg-3"
            )}
          >
            {line}
          </div>
        ))}
      </div>
      <span className="flex gap-2 text-sm leading-4.5 text-fg-3">
        <Lock className="mt-0.5 size-3.5 shrink-0" />
        {preview.count === 1
          ? "Creating the batch locks this entry and marks it exported. You can void the batch later to unlock it."
          : `Creating the batch locks these ${preview.count} entries and marks them exported. You can void the batch later to unlock them.`}
      </span>
    </>
  );
};

export const ExportDialog: FC<Props> = ({
  open,
  onOpenChange,
  initialRange,
  requireApproval,
  onOpenSettings
}) => {
  const [step, setStep] = useState<Step>(1);
  const [format, setFormat] = useState<ExportFormat>("ledes_1998b");
  const [range, setRange] = useState<RangeId>(initialRange);
  const [includeReleased, setIncludeReleased] = useState(false);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const dates = rangeFor(range, todayIso());
  const req: ExportRequest = {
    format,
    ...dates,
    include_released: requireApproval ? includeReleased : undefined,
    exclude_ids: excluded.length ? excluded : undefined
  };
  const { data } = useExportPreview(req, open);
  const providers = useProviders(open).data ?? [];
  const blocked =
    !data || data.blocking || (!data.count && !data.expense_count);

  const close = () => {
    onOpenChange(false);
    setStep(1);
    setExcluded([]);
  };

  const create = async () => {
    setBusy(true);
    try {
      const batch = await exportActions.create(req);
      if (batch) close();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent
        className="flex max-h-[min(700px,calc(100dvh-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-190"
        showCloseButton
        data-testid="export-dialog"
      >
        <div className="flex h-13 shrink-0 items-center gap-3.5 border-b border-line-2 px-4">
          <DialogTitle className="text-md">Export Time</DialogTitle>
          <DialogDescription className="sr-only">
            Choose a format, check Billing IDs, and preview the file.
          </DialogDescription>
          <Stepper step={step} />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4.5">
          {step === 1 ? (
            <FormatStep
              format={format}
              setFormat={setFormat}
              range={range}
              setRange={setRange}
              period={periodLabel(dates.from, dates.to)}
              requireApproval={requireApproval}
              includeReleased={includeReleased}
              setIncludeReleased={setIncludeReleased}
              providers={providers}
              companies={data?.companies ?? []}
            />
          ) : step === 2 ? (
            <CheckStep
              format={format}
              preview={data}
              excluded={excluded}
              setExcluded={setExcluded}
              onOpenSettings={onOpenSettings}
            />
          ) : data ? (
            <PreviewStep preview={data} />
          ) : (
            <Skeleton className="h-48" />
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 border-t border-line-2 p-3">
          <span
            className="text-sm text-fg-3 tabular-nums"
            data-testid="export-footer"
          >
            {data
              ? [
                  plural(data.count, "entry", "entries"),
                  formatHours(data.hours),
                  data.expense_count
                    ? plural(data.expense_count, "expense", "expenses")
                    : "",
                  formatMoney(data.amount)
                ]
                  .filter(Boolean)
                  .join(" · ")
              : "Counting…"}
          </span>
          <span className="flex-1" />
          {step > 1 ? (
            <Button
              variant="secondary"
              onClick={() => setStep((s) => (s === 3 ? 2 : 1))}
            >
              Back
            </Button>
          ) : (
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
          )}
          {step === 1 ? (
            <Button variant="primary" onClick={() => setStep(2)}>
              Check entries <ArrowRight />
            </Button>
          ) : step === 2 ? (
            <Button
              variant="primary"
              disabled={blocked}
              onClick={() => setStep(3)}
            >
              Preview file <ArrowRight />
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={blocked || busy}
              onClick={() => void create()}
            >
              {busy ? <Loader2 className="animate-spin" /> : <Download />}
              {busy ? "Creating the batch…" : "Create batch and download"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
